import { useAssetComponents } from "@/hooks/useAssetComponents";
import { cn } from "@/lib/utils";
import {
  selectMultiSelectedTempoElements,
  selectSelectedTempoElement,
  sendMessageToIFrame,
  setSelectedElementKey,
} from "@/store/iframeSlice";
import {
  AI_MODEL,
  AI_AGENT,
  useAiActionStore,
  SEARCH_SUPPORTED_MODELS,
} from "@/store/zustand/aiActionStore";
import {
  useAiContextStore,
  useIsChatLoading,
} from "@/store/zustand/aiContextStore";
import {
  useCanvasStore,
  useSelectedNavTreeNode,
} from "@/store/zustand/canvasStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import {
  useFeaturePrdForCanvas,
  useSupabaseStore,
} from "@/store/zustand/supabaseStore";
import { ACTION_TYPES } from "@/utils/ai/utils";
import { SCREEN_SIZE_NAME_TO_SIZE, STORYBOARD_TYPES } from "@/utils/constants";
import { useLoadCodebase } from "@/utils/devServerUtils";
import { track } from "@/utils/events/tracker";
import {
  getEditorLanguageForFilename,
  useActiveCanvas,
  useCanvasId,
} from "@/utils/utils";
import * as Lucide from "lucide-react";
import {
  ArrowUp,
  ChevronLeft,
  ImageUp,
  MousePointerClick,
  X,
  Brain,
  Globe,
  MessagesSquare,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Mention, MentionsInput } from "react-mentions";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";
import { toast } from "sonner";
import { useImageUploader } from "../AppHeader/FileUpload";
import SimpleMonacoEditor from "../CodeEditors/SimpleMonacoEditor";
import { useSupabase } from "../contexts/DatabaseSyncWrapper";
import { Button } from "../ui/button";
import ContextButton from "./components/ContextButton";
import { PRDVisualContext } from "./PRD/PRD";

import { setFigmaContext } from "@/store/supabaseSlice";
import { SuggestionsButton } from "./chat/SuggestionsButton";
import UsageIndicator from "./chat/UsageIndicator";
import { useUsageLimits } from "@/hooks/pricing/useUsageLimits";

import ActionDropdown from "./components/ActionDropDown";

import FeatureToggleMenu from "./components/FeatureToggleMenu";
import ActiveFeatureTags from "./components/ActiveFeatureTags";

import Icon, { ICONS } from "../Core/Icons";
import AiModelDropdown from "./components/AiModelDropDown";

import { UploadedFile } from "@/utils/types";

interface Props {
  addContextMode?: boolean;

  // If in chat, then it is the stand-in for the chat prompt
  isForChat?: boolean;
}

export const CREATE_STORYBOARD_INITIAL_WIDTH =
  SCREEN_SIZE_NAME_TO_SIZE["MacBook Pro 14”"].width;
export const CREATE_STORYBOARD_INITIAL_HEIGHT =
  SCREEN_SIZE_NAME_TO_SIZE["MacBook Pro 14”"].height;

const GenerateInput: React.FC<Props> = React.memo(
  ({ addContextMode, isForChat }) => {
    const canvasId = useCanvasId();
    const { supabaseClient } = useSupabase();

    const selectedElement = useSelector(selectSelectedTempoElement);
    const multiselectedElements = useSelector(selectMultiSelectedTempoElements);
    const setStopAiTrigger = useAiActionStore(
      (state) => state.setStopAiTrigger
    );

    const treeElementLookup = useCodebaseStore(
      (state) => state.treeElementLookup
    );

    const figmaContext = useSelector(
      (state: any) => state.supabaseStore.figmaContext
    );

    useEffect(() => {
      if (!figmaContext?.id || !supabaseClient) return;

      // delete any unused figma context for this canvas
      const deleteUnusedFigmaContexts = async () => {
        await supabaseClient
          .from("figma_ai_contexts")
          .delete()
          .eq("canvas_id", canvasId)
          .neq("id", figmaContext.id);
      };

      deleteUnusedFigmaContexts();
    }, [figmaContext, supabaseClient, canvasId]);
    // Canvas from DB
    const activeCanvas = useActiveCanvas();
    const {
      uploadedFiles,
      uploadFiles,
      clearUploadedFiles,
      FilePreviewerComponent,
      FileUploadComponent,
    } = useImageUploader();

    const setActionType = useAiActionStore((state) => state.setActionType);
    const actionType = useAiActionStore((state) => state.actionType);

    const isChatLoading = useIsChatLoading();
    const setSendChatMessage = useAiContextStore(
      (state) => state.setSendChatMessage
    );

    const dispatch = useDispatch<any>();

    const chatPromptInput = useAiActionStore((state) => state.chatPromptInput);
    const canvasPromptInput = useAiActionStore(
      (state) => state.canvasPromptInput
    );
    const _setPromptInput = useAiActionStore((state) => state.setPromptInput);

    const promptInput = isForChat ? chatPromptInput : canvasPromptInput;
    const setPromptInput = useCallback(
      (prompt: string) => {
        _setPromptInput(isForChat ? "chat" : "canvas", prompt);
      },
      [isForChat]
    );

    const [isDragging, setIsDragging] = useState(false);
    const promptPromiseRef = useRef(null);
    const [editModeHovered, setEditModeHovered] = useState(false);

    const featurePrd = useFeaturePrdForCanvas();
    const featurePrdVisualContext =
      featurePrd?.canvas_visual_context as any as PRDVisualContext;

    const contextWrapper = useRef<HTMLDivElement>(null);
    const [contextExpanded, setContextExpanded] = useState(false);
    const setChatContext = useAiContextStore((state) => state.setChatContext);
    const codeSnippet = useAiContextStore(
      (state) => state.chatContextCodeSnippet
    );
    const diffCodeSnippet = useAiContextStore(
      (state) => state.chatContextDiffCodeSnippet
    );

    const editorLanguageForCodeSnippet = React.useMemo(() => {
      if (!codeSnippet?.filename) {
        return null;
      }

      return getEditorLanguageForFilename(codeSnippet.filename);
    }, [codeSnippet?.filename]);

    const setAddContextMode = useLayoutStore(
      (state) => state.setAddContextMode
    );

    const onDbSnapshot = useSupabaseStore((store) => store.onDbSnapshot);

    const loadCodebase = useLoadCodebase();

    enum ReferenceObjectType {
      Storyboard,
      LucideIcon,
      Component,
    }

    interface ReferenceObject {
      id: string;
      type: ReferenceObjectType;
      display: string;
    }

    const references: ReferenceObject[] = useMemo(() => {
      const storyboards: ReferenceObject[] = (activeCanvas?.storyboards || [])
        .filter(
          (storyboard: any) =>
            storyboard.deleted !== true && !storyboard.preCreated
        )
        .filter(
          (storyboard: any) => storyboard.type !== STORYBOARD_TYPES.APPLICATION
        )
        .map((storyboard: any) => {
          return {
            id: `storyboard-${storyboard.id}`,
            type: ReferenceObjectType.Storyboard,
            display: storyboard.name,
          };
        });

      const components = Object.values(treeElementLookup)
        .filter((treeElement: any) => {
          return (
            treeElement.type === "component" ||
            treeElement.type === "storybook-component"
          );
        })
        .map((treeElement: any) => {
          return {
            id: `component-${treeElement.codebaseId}`,
            type: ReferenceObjectType.Component,
            display: treeElement.componentName,
          };
        });

      const icons: ReferenceObject[] = Object.keys(Lucide).map((iconName) => {
        return {
          id: `lucide-icon-${iconName}`,
          type: ReferenceObjectType.LucideIcon,
          display: iconName,
          icon: Lucide[iconName],
        };
      });

      return [...storyboards, ...components, ...icons];
    }, [activeCanvas, treeElementLookup]);

    const queryIcons = useCallback(
      (query, callback) => {
        if (query.length <= 2) return;

        const matches = references
          .filter(
            (reference) => reference.type === ReferenceObjectType.LucideIcon
          )
          .filter((reference) => {
            return (
              reference.display.toLowerCase().indexOf(query.toLowerCase()) > -1
            );
          });
        return matches;
      },
      [references]
    );

    const canvasComponentKey = useCanvasStore(
      (state) => state.canvasComponentKey
    );

    const { componentKeyToStories } = useAssetComponents();

    const setNewLayout = useLayoutStore((state) => state.setNewLayout);

    const inComponentsView = useMemo(() => {
      return !!canvasComponentKey;
    }, [canvasComponentKey]);

    const useReasoning = useAiContextStore((state) => state.useReasoning);
    const setUseReasoning = useAiContextStore((state) => state.setUseReasoning);

    const useSearch = useAiContextStore((state) => state.useSearch);
    const setUseSearch = useAiContextStore((state) => state.setUseSearch);

    const chatOnlyMode = useAiContextStore((state) => state.chatOnlyMode);
    const setChatOnlyMode = useAiContextStore((state) => state.setChatOnlyMode);

    const { remainingUsage } = useUsageLimits();

    const [isInputFocused, setIsInputFocused] = useState(false);
    const { reasoningModel, setReasoningModel, setCodeGenerationModel } =
      useAiActionStore();

    useEffect(() => {
      if (chatOnlyMode) {
        setCodeGenerationModel(AI_MODEL.GEMINI);
      } else if (
        reasoningModel === AI_MODEL.CLAUDE ||
        reasoningModel === AI_MODEL.GEMINI
      ) {
        setCodeGenerationModel(AI_MODEL.CLAUDE);
      } else if (reasoningModel === AI_MODEL.GEMINI_2_5_PRO) {
        setCodeGenerationModel(AI_MODEL.GEMINI_2_5_PRO);
      } else if (reasoningModel === AI_MODEL.GPT_4_1) {
        setCodeGenerationModel(AI_MODEL.GPT_4_1);
      } else if (reasoningModel === AI_MODEL.CLAUDE_4_0_SONNET) {
        setCodeGenerationModel(AI_MODEL.CLAUDE_4_0_SONNET);
      } else {
        setCodeGenerationModel(AI_MODEL.CLAUDE_3_5_SONNET);
      }
    }, [chatOnlyMode, reasoningModel]);

    useEffect(() => {
      if (SEARCH_SUPPORTED_MODELS.has(reasoningModel) && useSearch) {
        setUseSearch(true);
      }
    }, [reasoningModel]);

    useEffect(() => {
      // The one for chat does not affect this
      if (isForChat) {
        return;
      }

      if (addContextMode) {
        setActionType(ACTION_TYPES.ADD_CONTEXT);
        return;
      }

      if (selectedElement.isEmpty()) {
        setActionType(ACTION_TYPES.GENERATE);
      } else {
        setActionType(ACTION_TYPES.EDIT);
      }
    }, [addContextMode]);

    useEffect(() => {
      // The one for chat does not affect this
      if (isForChat) {
        return;
      }

      if (addContextMode) {
        setActionType(ACTION_TYPES.ADD_CONTEXT);
        return;
      }

      if (!selectedElement.isEmpty()) {
        setActionType(ACTION_TYPES.EDIT);
      }
    }, [selectedElement.getKey()]);

    useEffect(() => {
      // The one for chat does not affect this
      if (isForChat) {
        return;
      }

      // This will make sure that, Whenever the user clicks on the canvas, when the edit mode is active then the action type will be set to generate
      if (actionType === ACTION_TYPES.EDIT && selectedElement.isEmpty()) {
        setActionType(ACTION_TYPES.GENERATE);
      }
    }, [selectedElement]);

    const sendEditAIRequest = async (
      prompt: string,
      filesToAttach: UploadedFile[]
    ) => {
      if (isChatLoading) {
        toast.info("There's already a prompt in progress!");
        return;
      }

      if (!prompt?.trim()) {
        return;
      }

      // User selection
      const elements = multiselectedElements?.length
        ? multiselectedElements
        : [selectedElement];

      const treeElements = !selectedElement.isStoryboard()
        ? elements.map((element) => treeElementLookup[element.codebaseId])
        : [];
      const storyboardIds = selectedElement.isStoryboard()
        ? elements.map((element) => element.storyboardId)
        : [];

      // @ts-expect-error TS18047
      const storyboards = activeCanvas.storyboards.filter((sb) =>
        storyboardIds.includes(sb.id)
      );
      const totalUserSelection = treeElements
        .concat(storyboards)
        .filter((element) => element);

      if (!totalUserSelection?.length) {
        toast.error("Selected element not found in codebase, refreshing.");
        // If there is an error it could be because the codebase is out of sync, automatically reload the codebase
        // Mark this as flushing even though it's technically not to show the syncing indicator and to prevent more changes
        await loadCodebase(true);
        return;
      }

      const sent = setSendChatMessage({
        sendChatMessage: `Please apply this change to the items I selected: ${prompt}`,
        sendChatMessageFiles: filesToAttach,
      });

      if (sent) {
        setNewLayout(LeftTab.AIChat);
        track("AI_PROMPT_REQUESTED", {
          type: "AI_CHAT_IN_CANVAS_EDIT",
          prompt,
          filesToAttach,
        });
        setPromptInput("");
        clearUploadedFiles();
      }
    };

    const sendGenerateNewVariantRequest = async (
      prompt: string,
      filesToAttach: UploadedFile[]
    ) => {
      if (!prompt?.trim()) {
        return;
      }

      if (isChatLoading) {
        return;
      }

      toast.warning("Not implemented");

      // @ts-expect-error TS2538
      const stories = componentKeyToStories[canvasComponentKey] || [];
      const filename = stories[0]?.filename;

      const injectedPrompt = `
      I am looking at various stories for a React component. Help me generate a new variant in the stories file for this component.
      You should use .stories.tsx file and the component itself for context.

      My prompt: ${prompt}

      The stories I am looking at are in the file ${filename}.
    `;

      const sent = setSendChatMessage({
        sendChatMessage: injectedPrompt,
        sendChatMessageFiles: filesToAttach,
      });

      if (sent) {
        setNewLayout(LeftTab.AIChat);
        track("AI_PROMPT_REQUESTED", {
          type: "AI_CHAT_IN_CANVAS_GENERATE_VARIANT",
          prompt,
          filesToAttach,
        });
        setPromptInput("");
        clearUploadedFiles();
      }
    };

    const sendGenerateAIRequest = async (
      prompt: string,
      filesToAttach: UploadedFile[]
    ) => {
      if (actionType !== ACTION_TYPES.GENERATE) {
        console.error("Invalid action type");
        throw new Error("Invalid action type");
      }

      if (isChatLoading) {
        return;
      }

      if (!prompt || prompt.trim() === "") {
        return;
      }

      // @ts-expect-error TS2322
      promptPromiseRef.current = new AbortController();

      const sent = setSendChatMessage({
        sendChatMessage: `Please create a brand new component for me and create a new Storyboard to showcase it using this prompt: ${prompt}`,
        sendChatMessageFiles: filesToAttach,
      });
      if (sent) {
        setNewLayout(LeftTab.AIChat);
        track("AI_PROMPT_REQUESTED", {
          type: "AI_CHAT_IN_CANVAS_GENERATE",
          prompt,
          filesToAttach,
        });
        setPromptInput("");
        clearUploadedFiles();
      }
    };

    const [inputMounted, setInputMounted] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
      if (inputRef.current) {
        // @ts-expect-error TS2339
        inputRef.current.style.height = "auto"; // Reset height to shrink if text is deleted
        // @ts-expect-error TS2339
        inputRef.current.style.height = `${inputRef.current.scrollHeight}px`; // Expand to fit content
      }
    }, [promptInput, actionType]);

    useEffect(() => {
      const handleFocus = () => setIsInputFocused(true);
      const handleBlur = () => setIsInputFocused(false);

      const inputElement = inputRef.current;
      if (inputElement) {
        // @ts-expect-error TS2339
        inputElement.addEventListener("focus", handleFocus);
        // @ts-expect-error TS2339
        inputElement.addEventListener("blur", handleBlur);
      }

      // @ts-expect-error TS2339
      inputRef.current?.focus();

      return () => {
        if (inputElement) {
          // @ts-expect-error TS2339
          inputElement.removeEventListener("focus", handleFocus);
          // @ts-expect-error TS2339
          inputElement.removeEventListener("blur", handleBlur);
        }
      };
    }, [inputMounted]);

    useEffect(() => {
      // When the chat starts loading, reset the selection
      if (isChatLoading) {
        dispatch(setSelectedElementKey(null));
      }
    }, [isChatLoading]);

    const handleSubmit = useCallback(async () => {
      if (!featurePrd || !supabaseClient) {
        return;
      }

      if (!promptInput || promptInput.trim() === "") {
        return;
      }

      if (isChatLoading) {
        return;
      }

      const filesToAttach = uploadedFiles;

      // Just send as a regular chat message
      const allFiles = [...filesToAttach].filter(Boolean);

      if (figmaContext?.image_url) {
        allFiles.push({
          url: figmaContext?.image_url,
          name: "figma_image",
          type: "image",
          mimeType: "image/png",
        });
      }

      if (isForChat) {
        const sent = setSendChatMessage({
          sendChatMessage: `${promptInput}${
            figmaContext && Object.keys(figmaContext).length > 0
              ? `\n<figma_input>\nFigma Context: ${JSON.stringify(
                  figmaContext
                )}\n</figma_input>\n`
              : ""
          }`,
          // @ts-expect-error TS2322
          sendChatMessageFiles: allFiles?.length ? allFiles : null,
        });

        if (sent) {
          setPromptInput("");
          clearUploadedFiles();
          track("AI_PROMPT_REQUESTED", {
            type: "AI_CHAT",
            prompt: promptInput,
            filesToAttach,
          });
        }
        return;
      }

      if (actionType === ACTION_TYPES.ADD_CONTEXT) {
        let context;
        if (selectedElement.isStoryboard()) {
          context = promptInput;
        } else {
          const selectedTreeElement =
            treeElementLookup[selectedElement.codebaseId];
          if (!selectedTreeElement) {
            console.error("Could not find selected tree element");
            toast.error("Could not find selected element in codebase");
            return;
          }

          context = `Element ${selectedTreeElement.jsxName} on line ${
            selectedTreeElement.startLine
          } in file ${
            selectedTreeElement.filename.split(canvasId + "/")[1]
          } - desired action: "${promptInput}"`;
        }

        const newContext = [
          ...(featurePrdVisualContext || []),
          {
            text: context,
            file_urls: filesToAttach,
          },
        ];

        try {
          onDbSnapshot("featurePrds", {
            newItems: [{ ...featurePrd, canvas_visual_context: newContext }],
          });

          await supabaseClient
            .from("feature_prd")
            .update({
              canvas_visual_context: newContext as any,
            })
            .eq("canvas_id", canvasId);

          setAddContextMode(false);

          setPromptInput("");
          dispatch(setSelectedElementKey(null));
        } catch (e) {
          console.error(e);
          toast.error("Could not add context to EngSpec");
          return;
        }

        return;
      }

      if (actionType === ACTION_TYPES.EDIT) {
        sendEditAIRequest(promptInput, filesToAttach);
      } else if (inComponentsView) {
        sendGenerateNewVariantRequest(promptInput, filesToAttach);
      } else {
        sendGenerateAIRequest(promptInput, filesToAttach);
      }
    }, [
      featurePrd,
      promptInput,
      isChatLoading,
      uploadedFiles,
      actionType,
      selectedElement,
      treeElementLookup,
      canvasId,
      supabaseClient,
    ]);

    const handleInput = useCallback(
      (event) => {
        if (isChatLoading) {
          return;
        }

        setPromptInput(event.target.value.trimStart());
      },
      [isChatLoading, handleSubmit]
    );

    useEffect(() => {
      dispatch(
        sendMessageToIFrame({
          type: "executeFunction",
          payload: {
            functionName: "setAiContext",
            args: [
              actionType === ACTION_TYPES.EDIT ||
                actionType === ACTION_TYPES.ADD_CONTEXT,
            ],
          },
        })
      );

      dispatch(
        sendMessageToIFrame({
          type: "executeFunction",
          payload: {
            functionName: "updateOutlines",
          },
        })
      );
    }, [actionType]);

    useEffect(() => {
      return () => {
        setActionType(ACTION_TYPES.GENERATE);
        dispatch(
          sendMessageToIFrame({
            type: "executeFunction",
            payload: {
              functionName: "setAiContext",
              args: [false],
            },
          })
        );

        dispatch(
          sendMessageToIFrame({
            type: "executeFunction",
            payload: {
              functionName: "updateOutlines",
            },
          })
        );
      };
    }, []);

    const clearContext = () => {
      dispatch(setSelectedElementKey(null));
    };

    const clearFigmaContext = async () => {
      if (!supabaseClient) {
        return;
      }

      await supabaseClient
        .from("figma_ai_contexts")
        .delete()
        .eq("canvas_id", canvasId);
      dispatch(setFigmaContext(null));
    };

    const actionText = useMemo(() => {
      if (isChatLoading) {
        return "Replying...";
      }

      if (isForChat) {
        return "Ask anything";
      }

      if (isDragging) {
        return "Upload an image to use as context";
      }

      if (inComponentsView) {
        return "Generate a blue variant of this button";
      }

      if (actionType === ACTION_TYPES.GENERATE) {
        return "Create a chat interface";
      } else if (actionType === ACTION_TYPES.EDIT) {
        if (selectedElement.isEmpty()) {
          return "Select an element on the canvas to edit";
        } else {
          return "Change the color of this button to blue";
        }
      } else if (actionType === ACTION_TYPES.ADD_CONTEXT) {
        if (selectedElement.isEmpty()) {
          return "Select an element to add context for";
        } else {
          return "Change the color to blue";
        }
      }
    }, [
      actionType,
      inComponentsView,
      selectedElement,
      isDragging,
      isChatLoading,
    ]);

    const selectedNavTreeNode = useSelectedNavTreeNode();

    const handleImagePaste = (
      event: React.ClipboardEvent<HTMLTextAreaElement | HTMLDivElement>
    ) => {
      if (event.defaultPrevented) return;

      const items = event.clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            event.preventDefault();
            uploadFiles([file]);
            return;
          }
        }
      }
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
      setIsDragging(false);
      event.preventDefault();
      event.stopPropagation();
      const files = event.dataTransfer.files;
      if (files.length > 0 && files[0].type.startsWith("image/")) {
        uploadFiles(Array.from(files));
      }
    };

    const handleDragEnter = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(true);
    };

    const handleDragOver = (event) => {
      event.preventDefault(); // Necessary to allow the drop event
      event.stopPropagation();
      if (!isDragging) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
    };

    const container = useRef(null);

    const showAddedContextButton =
      (actionType === ACTION_TYPES.EDIT ||
        actionType === ACTION_TYPES.ADD_CONTEXT ||
        isForChat) &&
      !selectedElement.isEmpty();

    const showFigmaContextButton =
      figmaContext && Object.keys(figmaContext).length > 0;

    const addedContextButton = showAddedContextButton ? (
      <ContextButton
        element={selectedElement}
        // @ts-expect-error TS2322
        navTreeNode={selectedNavTreeNode}
        onClick={() => {
          clearContext();
          if (!addContextMode) {
            setActionType(ACTION_TYPES.GENERATE);
          }
        }}
      />
    ) : null;

    const figmaContextButton = showFigmaContextButton ? (
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          "h-4 text-primary-n-500 bg-[#6448EF33] p-2 py-3 flex gap-1 text-3xs font-medium mb-0.5 cursor-pointer",
          "hover:text-primary-n-500 hover:bg-[#6448EF33]"
        )}
        onClick={clearFigmaContext}
      >
        <img src="/images/figma-logo.svg" alt="Figma" className="h-3 w-3" />
        <span>{figmaContext?.component_name}</span>
        <X className="stroke-primary-n-500 h-3 w-3" />
      </Button>
    ) : null;

    const canGenerateOrAddContext =
      actionType === ACTION_TYPES.GENERATE ||
      actionType === ACTION_TYPES.ADD_CONTEXT;

    // Function to determine if the FileUploadComponent should be shown
    const shouldShowFileUpload = () => {
      const canEdit =
        actionType === ACTION_TYPES.EDIT && !selectedElement.isEmpty();
      const canShowFileUpload = !isDragging && remainingUsage.hasPromptsLeft;

      return (canGenerateOrAddContext || canEdit) && canShowFileUpload;
    };

    return (
      <div className="flex w-full flex-col">
        <div className="primary rounded-lg bg-background-transparent">
          {isForChat && (
            <div className="flex items-center gap-1 justify-center pt-1">
              {<UsageIndicator isForChat={isForChat} />}
              <SuggestionsButton
                onSelectSuggestion={(suggestion) => {
                  const suggestionBody = `${suggestion.short_title}\n\n${suggestion.detailed_description}\n\nMake this change for me in the codebase if you can`;
                  setPromptInput(suggestionBody);
                }}
              />
            </div>
          )}

          <div
            className={cn(
              "flex flex-col p-1.5 h-auto bg-background-primary w-full border rounded-lg shadow-lg pointer-events-auto relative",
              isDragging && "border border-primary-500 border-dashed",
              isForChat && "border rounded-lg",
              isForChat && isInputFocused && "border-primary-0"
            )}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onPaste={handleImagePaste}
          >
            {(actionType === ACTION_TYPES.EDIT ||
              actionType === ACTION_TYPES.ADD_CONTEXT) &&
            selectedElement.isEmpty() ? (
              <div>
                <div className="relative p-2 border border-dashed border-secondary-n-700 rounded-lg">
                  <X
                    data-tooltip-content={"Exit Pick & Edit"}
                    data-tooltip-id="tooltip"
                    data-tooltip-place="bottom"
                    onClick={() => {
                      if (actionType === ACTION_TYPES.EDIT) {
                        clearContext();
                        setActionType(ACTION_TYPES.GENERATE);
                      }
                    }}
                    className="w-4 h-4 text-secondary-n-600 absolute right-1 top-1 cursor-pointer"
                  />
                  <div className="h-full flex flex-col items-center gap-2 justify-center max-w-48 mx-auto">
                    <Lucide.SquareMousePointer
                      data-tooltip-id="tooltip"
                      data-tooltip-place="bottom"
                      className="w-4 h-4 text-secondary-n-600"
                    />
                    <p className="text-xs text-secondary-n-50 text-center">
                      Select a storyboard or element to give AI more context.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                {codeSnippet && (
                  <div
                    className={cn(
                      "flex flex-col grow mx-1 bg-secondary-200 mt-1 px-1 rounded-md relative mb-1"
                    )}
                  >
                    <div className="flex flex-row justify-between w-full items-center p-1 relative">
                      <div
                        className={cn(
                          "bg-secondary-500 text-secondary-800 rounded-md text-2xs px-1.5 py-0.5 grow-0 w-auto"
                        )}
                      >
                        {"Code: " +
                          (codeSnippet.filename?.includes(canvasId)
                            ? codeSnippet.filename.split(canvasId + "/")[1]
                            : codeSnippet.filename) +
                          ":" +
                          codeSnippet.startLine +
                          "-" +
                          codeSnippet.endLine}
                      </div>
                      <Lucide.XIcon
                        className={cn(
                          "h-4 w-4 cursor-pointer text-secondary-800 hover:text-white"
                        )}
                        onClick={() => {
                          // @ts-expect-error TS2345
                          setChatContext(null);
                        }}
                      />
                    </div>
                    <div
                      ref={contextWrapper}
                      className={cn(
                        "flex transition-all",
                        !contextExpanded && "max-h-40 overflow-y-hidden",
                        contextExpanded &&
                          "max-h-80 overflow-y-scroll hide-scrollbar"
                      )}
                    >
                      {codeSnippet ? (
                        <SimpleMonacoEditor
                          theme={"tempo-theme"}
                          value={codeSnippet.contents}
                          additionalOptions={{
                            readOnly: true,
                            lineNumbers: (num) =>
                              (codeSnippet.startLine + num - 1).toString(),
                          }}
                          // @ts-expect-error TS2322
                          language={editorLanguageForCodeSnippet}
                          originalValue={diffCodeSnippet?.contents}
                          diff={Boolean(diffCodeSnippet)}
                        />
                      ) : null}
                    </div>
                    <div
                      className={cn(
                        "absolute bottom-0 left-0 w-full flex items-center justify-center pt-8 pb-1 cursor-pointer bg-gradient-to-b from-transparent to-secondary-0",
                        contextExpanded && "static bg-none !py-2"
                      )}
                      onClick={() => setContextExpanded(!contextExpanded)}
                    >
                      <Lucide.ChevronDownIcon
                        className={cn(
                          "size-4 text-secondary-900 hover:text-white transition-all",
                          contextExpanded && "rotate-180"
                        )}
                      />
                    </div>
                  </div>
                )}
                <div
                  className={cn(
                    "flex flex-row items-center justify-start gap-3 pl-2",
                    (showAddedContextButton && isForChat) ||
                      uploadedFiles.length > 0
                      ? "pt-1"
                      : null
                  )}
                >
                  <FilePreviewerComponent />
                  {isForChat && showAddedContextButton
                    ? addedContextButton
                    : null}
                  {showFigmaContextButton ? figmaContextButton : null}
                </div>
                <div
                  className={cn(
                    "flex w-full flex-row justify-center items-end text-2xs text-white leading-none relative"
                  )}
                >
                  <div ref={container}></div>
                  <div
                    className={cn(
                      "flex items-end justify-center transition-all duration-200 ease-in-out w-full max-w-full gap-0.5"
                    )}
                  >
                    <div
                      className={cn(
                        "flex-1 flex items-center gap-0.5 min-w-0",
                        !isForChat && "min-w-[10rem] ml-0"
                      )}
                    >
                      <MentionsInput
                        className={cn(
                          "mentions-input min-h-7 mx-1 mb-4 focus:outline-none outline-none bg-transparent text-xs font-medium w-full",
                          !isForChat && "max-w-[18rem] ml-0",
                          isForChat && "flex-1"
                        )}
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          paddingRight: "8px",
                          height: remainingUsage.hasPromptsLeft
                            ? "auto"
                            : "4rem",
                        }}
                        value={promptInput}
                        inputRef={(ref) => {
                          inputRef.current = ref;
                          setInputMounted(true);
                        }}
                        onChange={handleInput}
                        placeholder={
                          remainingUsage.hasPromptsLeft
                            ? actionText
                            : "No prompts available. You can still make changes in Tempo, but AI-powered features won't be available."
                        }
                        disabled={
                          (actionType !== ACTION_TYPES.GENERATE &&
                            selectedElement.isEmpty()) ||
                          isChatLoading ||
                          !remainingUsage.hasPromptsLeft
                        }
                        rows={1}
                        onKeyDown={(e: any) => {
                          if (
                            (e.key === "Enter" || e.keyCode === 13) &&
                            !e.shiftKey
                          ) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSubmit();
                          }
                        }}
                        // Renders the SuggestionList above the cursor if there is not enough space below
                        allowSuggestionsAboveCursor
                        suggestionsPortalHost={container.current}
                        onPaste={handleImagePaste}
                      >
                        <Mention
                          data={references.filter(
                            (reference) =>
                              reference.type ===
                                ReferenceObjectType.Storyboard ||
                              reference.type === ReferenceObjectType.Component
                          )}
                          displayTransform={(id, display) => {
                            return `@${display}`;
                          }}
                          trigger="@"
                          renderSuggestion={(
                            suggestion,
                            search,
                            highlightedDisplay
                          ) => (
                            <div className="flex h-max">
                              {highlightedDisplay}
                            </div>
                          )}
                          markup="@[__display__](__id__)"
                          className="bg-primary-200"
                          appendSpaceOnAdd
                        />
                        <Mention
                          data={queryIcons}
                          displayTransform={(id, display) => {
                            return `\<${display} \/\>`;
                          }}
                          markup=":[__display__](__id__)"
                          trigger=":"
                          renderSuggestion={(
                            suggestion,
                            search,
                            highlightedDisplay
                          ) => {
                            const Icon = Lucide[suggestion.display];
                            return (
                              <div className="flex flex-row justify-between items-center space-x-1">
                                <span>{highlightedDisplay}</span>
                                {Icon && <Icon className="w-4 h-4" />}
                              </div>
                            );
                          }}
                          className="bg-primary-200"
                          appendSpaceOnAdd
                        />
                      </MentionsInput>
                    </div>
                    {isDragging && (
                      <Button
                        variant="ghost"
                        className="h-7 w-7 p-1 rounded-md"
                      >
                        <ImageUp
                          strokeWidth={1}
                          className="w-5 h-5 text-icons-muted group-hover:text-icons-primary"
                        />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="bg-background-primary rounded-md flex gap-1 p-1 items-center">
                  <ActionDropdown
                    shouldShowFileUpload={shouldShowFileUpload()}
                    handleAddContext={() => {
                      if (isChatLoading) {
                        return;
                      }

                      setEditModeHovered(false);
                      if (addContextMode) {
                        clearContext();
                        return;
                      }

                      if (actionType === ACTION_TYPES.EDIT) {
                        clearContext();
                        setActionType(ACTION_TYPES.GENERATE);
                      } else {
                        clearContext();
                        setActionType(ACTION_TYPES.EDIT);
                      }
                    }}
                    FileUploadComponent={FileUploadComponent}
                    value={reasoningModel}
                    onChange={(value: AI_MODEL) => {
                      setReasoningModel(value);
                    }}
                  />
                  <FeatureToggleMenu
                    isChatLoading={isChatLoading}
                    useReasoning={useReasoning}
                    setUseReasoning={setUseReasoning}
                    useSearch={useSearch}
                    setUseSearch={setUseSearch}
                    useChatOnly={chatOnlyMode}
                    setUseChatOnly={setChatOnlyMode}
                    reasoningModel={reasoningModel}
                  />
                  <div>
                    <AiModelDropdown
                      reasoningModel={reasoningModel}
                      setReasoningModel={setReasoningModel}
                    />
                  </div>
                  <ActiveFeatureTags
                    useReasoning={useReasoning}
                    useSearch={useSearch}
                    useChatOnly={chatOnlyMode}
                    setUseReasoning={setUseReasoning}
                    setUseSearch={setUseSearch}
                    setUseChatOnly={setChatOnlyMode}
                    isChatLoading={isChatLoading}
                    reasoningModel={reasoningModel}
                  />

                  <div className="flex items-center justify-end gap-0.5 space-x-0.5 ml-auto">
                    {remainingUsage.hasPromptsLeft && (
                      <div
                        className={cn(
                          "transition-all duration-200 pl-1 shrink-0 flex items-center",
                          (actionType === ACTION_TYPES.EDIT ||
                            actionType === ACTION_TYPES.ADD_CONTEXT) &&
                            selectedElement.isEmpty() &&
                            "w-0 hidden cursor-not-allowed pointer-events-none"
                        )}
                      >
                        {isChatLoading ? (
                          <Button
                            variant="primary"
                            onClick={setStopAiTrigger}
                            className="h-7 w-7 p-1 rounded-md shrink-0"
                          >
                            <Icon
                              icon={ICONS.CIRCLE_STOP}
                              className="h-4 w-4 text-secondary-900"
                            />
                          </Button>
                        ) : (
                          <Button
                            variant="primary"
                            disabled={!promptInput.trim()}
                            className="h-7 w-7 p-1 rounded-md shrink-0"
                            onClick={handleSubmit}
                          >
                            <ArrowUp
                              data-tooltip-content="Submit"
                              data-tooltip-id="tooltip"
                              data-tooltip-place="bottom"
                              className={
                                "size-4 text-secondary-900 cursor-pointer hover:text-white focus:outline-none"
                              }
                            />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);
GenerateInput.displayName = "GenerateInput";

export default GenerateInput;
